// Editor de grupo Ampli (espec. 4.2 e 8.5). Um grupo é um conjunto de insumos da
// obra montado por descrição (`description_contains`, opcionalmente dinâmico) ou por
// escolha explícita de insumos — e um insumo pertence a NO MÁXIMO um grupo Ampli por
// obra. É essa invariante que o back-end defende com 409 `group_overlap`; aqui a tela
// traduz o conflito em duas saídas honestas:
//
//   - conflito `conflictingGroup.movable === true` (todo insumo do grupo conflitante
//     está num grupo EXPLÍCITO) -> "Mover para este grupo" reenvia o MESMO payload
//     com `moveOnOverlap: true` e o servidor tira os insumos do grupo antigo;
//   - `movable` ausente/false (algum insumo está num grupo dinâmico), ou 409 que
//     persiste depois do move -> não existe "mover" que resolva, então a tela pede
//     ajuste do critério. Sem laço de retry.
//
// Padrão de offcanvas herdado do `RuleEditorOffcanvas`: `role="dialog"`, foco que
// entra ao abrir e volta ao fechar, Escape fecha, e modo apresentação é gate duro.
import { useEffect, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";

import { fetchDisbursementTargets, saveDisbursementGroup } from "./disbursementApi";
import { normalizeText } from "./engine";
import { disbursementErrorDetail } from "./errorMessages";
import { money } from "./format";

const SEARCH_DEBOUNCE_MS = 350;

const CRITERION_KINDS = Object.freeze([
  { value: "description_contains", label: "Pela descrição do insumo" },
  { value: "explicit", label: "Escolhendo os insumos" },
]);

const SAVE_ERROR_PREFIX = "Não foi possível salvar o grupo.";
const SAVE_ERROR_TEXT = `${SAVE_ERROR_PREFIX} Tente novamente.`;

// Mapa compartilhado com o editor de regra e a seção do mês (`errorMessages.js`):
// os três falam com os mesmos handlers e recebem os mesmos códigos.
const saveErrorText = (result) => {
  const detail = disbursementErrorDetail(result?.errorCode);
  return detail ? `${SAVE_ERROR_PREFIX} ${detail}` : SAVE_ERROR_TEXT;
};

const resourceIdOf = (key) => String(key ?? "").replace(/^resource:/, "");

// `dynamic` só tem default `true` quando NÃO existe grupo gravado ainda (criação):
// nesse caso o campo nem existe. Editando um grupo existente o valor vem do
// back-end tal e qual — campo ausente é `false` (grupos explícitos nunca gravam
// `dynamic: true`, então isso também cobre migração de dado velho).
const dynamicDefaultFor = (group) => (group ? group.dynamic === true : true);

// Grupo explícito gravado sem `criterion.resourceIds` (ou lista vazia) é linha
// truncada/desatualizada: não dá pra pré-preencher a seleção com segurança, então a
// tela avisa e não deixa salvar por cima de uma composição que ela não carregou.
const explicitMembersMissingFor = (group) => {
  if (!group || group.criterion?.kind !== "explicit") return false;
  const ids = group.criterion?.resourceIds;
  return !Array.isArray(ids) || ids.length === 0;
};

/**
 * O grupo em conflito vem em `conflictingGroup: { groupId, name, movable }` (contrato
 * do BE): `movable === true` só quando TODO insumo sobreposto está num grupo
 * EXPLÍCITO — qualquer outro valor (ausente ou `false`) significa que mover não
 * resolve. `groupName` é o atalho tolerado e a string crua é a última defesa —
 * nenhuma delas pode virar "undefined" no meio da frase que o cliente lê.
 */
const conflictOf = (result) => {
  const group = result?.conflictingGroup;
  const name = (typeof group === "string" ? group : group?.name)
    ?? result?.groupName
    ?? null;
  return { name: name || "outro grupo", movable: group?.movable === true };
};

/** Folha para o dinheiro: `money()` lê contexto, então nunca dentro de laço/condição. */
const CandidatePlanned = ({ cents }) => <span>{money(cents)}</span>;

const CandidateRow = ({ candidate, selectable, selected, matches, onToggle }) => (
  <label className="ampli-group-editor__member">
    <input
      type="checkbox"
      aria-label={candidate.label}
      checked={selectable ? selected : matches}
      disabled={!selectable}
      onChange={(event) => onToggle(candidate.key, event.target.checked)}
    />
    <span className="ampli-group-editor__member-label">{candidate.label}</span>
    <span className="ampli-group-editor__member-value">
      <CandidatePlanned cents={candidate.plannedTotalCents ?? candidate.plannedCents} />
    </span>
  </label>
);

const GroupEditorOffcanvas = ({
  open,
  onClose,
  project = null,
  projectId = null,
  readOnly = false,
  initialGroup = null,
  onSaved,
}) => {
  const [name, setName] = useState(initialGroup?.name ?? "");
  const [kind, setKind] = useState(initialGroup?.criterion?.kind ?? "description_contains");
  const [value, setValue] = useState(initialGroup?.criterion?.value ?? "");
  const [resourceIds, setResourceIds] = useState(initialGroup?.criterion?.resourceIds ?? []);
  const [dynamic, setDynamic] = useState(() => dynamicDefaultFor(initialGroup));
  const [explicitMembersMissing, setExplicitMembersMissing] = useState(
    () => explicitMembersMissingFor(initialGroup),
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidatesError, setCandidatesError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // `{ name, movable }` — `movable: false` (ou ausente) significa "mover não
  // resolve": pede ajuste do critério em vez de oferecer um retry que o servidor já
  // recusou.
  const [overlap, setOverlap] = useState(null);
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Semeia o formulário quando o editor abre para OUTRO grupo (ou para um novo): a
  // chave é derivada, então um re-render do pai com o mesmo grupo não apaga o que o
  // usuário está digitando.
  const seedKey = `${open ? "open" : "closed"}:${initialGroup?.groupId ?? "new"}`;
  useEffect(() => {
    setName(initialGroup?.name ?? "");
    setKind(initialGroup?.criterion?.kind ?? "description_contains");
    setValue(initialGroup?.criterion?.value ?? "");
    setResourceIds(initialGroup?.criterion?.resourceIds ?? []);
    setDynamic(dynamicDefaultFor(initialGroup));
    setExplicitMembersMissing(explicitMembersMissingFor(initialGroup));
    setSearch("");
    setDebouncedSearch("");
    setSaveError(null);
    setOverlap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Foco entra no painel ao abrir e volta pro gatilho ao fechar (espec. 8.5).
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const previous = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (previous && typeof previous.focus === "function" && document.body.contains(previous)) {
        previous.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    // `cancelled` é a guarda contra resposta desatualizada: trocar a busca enquanto
    // uma requisição voa não pode deixar a resposta antiga sobrepor a mais recente.
    let cancelled = false;
    (async () => {
      const payload = await fetchDisbursementTargets(projectId, {
        level: "resource",
        search: debouncedSearch,
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        setCandidates([]);
        setCandidatesError(true);
        return;
      }
      setCandidatesError(false);
      setCandidates(payload.items);
    })();
    return () => { cancelled = true; };
  }, [open, projectId, debouncedSearch]);

  if (!open) return null;

  // Modo apresentação é gate duro: um `readOnly={false}` do chamador não o destrava.
  const isReadOnly = readOnly || isPresentationProject(project);
  const isExplicit = kind === "explicit";
  const normalizedValue = normalizeText(value);
  const criterion = isExplicit
    ? { kind: "explicit", resourceIds }
    : { kind: "description_contains", value: normalizedValue };
  const titleText = initialGroup ? "Editar grupo Ampli" : "Novo grupo Ampli";
  const canSave = !isReadOnly
    && !(isExplicit && explicitMembersMissing)
    && name.trim().length > 0
    && (isExplicit ? resourceIds.length > 0 : normalizedValue.length > 0);

  const clearFeedback = () => {
    setSaveError(null);
    setOverlap(null);
  };

  const submit = async ({ moveOnOverlap = false } = {}) => {
    if (!canSave || saving) return;
    setSaving(true);
    setSaveError(null);
    const payload = {
      ...(initialGroup?.groupId ? { groupId: initialGroup.groupId } : {}),
      name: name.trim(),
      criterion,
      dynamic: isExplicit ? false : dynamic,
      ...(moveOnOverlap ? { moveOnOverlap: true } : {}),
    };
    const result = await saveDisbursementGroup(projectId, payload);
    setSaving(false);
    if (result?.code === 409 && result.errorCode === "group_overlap") {
      const conflict = conflictOf(result);
      // Já tentamos mover, ou o BE não marcou o conflito como `movable`: ele vai
      // recusar de novo. Nada de oferecer o mesmo botão outra vez.
      setOverlap({ name: conflict.name, movable: !moveOnOverlap && conflict.movable });
      return;
    }
    if (!result || (result.code !== 200 && result.code !== 202)) {
      setOverlap(null);
      setSaveError(saveErrorText(result));
      return;
    }
    setOverlap(null);
    await onSaved?.(result);
    onClose?.();
  };

  const toggleResource = (key, checked) => {
    clearFeedback();
    const id = resourceIdOf(key);
    setResourceIds((current) => (
      checked ? [...new Set([...current, id])] : current.filter((item) => item !== id)
    ));
  };

  const membersLegend = isExplicit
    ? `${resourceIds.length} insumos selecionados`
    : "Insumos que casam com a descrição";

  return (
    <div className="offcanvas active">
      <div
        className="offcanvas-container ampli-group-editor"
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
        tabIndex={-1}
        ref={panelRef}
      >
        <hgroup className="offcanvas-header">
          <div>
            <h2>{titleText}</h2>
            <p className="card-label">
              Um insumo pertence a no máximo um grupo Ampli por obra.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <i className="icon icon-width-24 icon-times background-accent" aria-hidden={true}></i>
          </button>
        </hgroup>

        <div className="offcanvas-content">
          <label>
            <span>Nome do grupo</span>
            <input
              type="text"
              aria-label="Nome do grupo"
              value={name}
              disabled={isReadOnly}
              onChange={(event) => { clearFeedback(); setName(event.target.value); }}
              placeholder="Esquadrias"
            />
          </label>

          <label>
            <span>Como o grupo é montado</span>
            <select
              aria-label="Como o grupo é montado"
              value={kind}
              disabled={isReadOnly}
              onChange={(event) => { clearFeedback(); setKind(event.target.value); }}
            >
              {CRITERION_KINDS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {!isExplicit ? (
            <>
              <label>
                <span>Descrição contém</span>
                <input
                  type="text"
                  aria-label="Descrição contém"
                  value={value}
                  disabled={isReadOnly}
                  onChange={(event) => { clearFeedback(); setValue(event.target.value); }}
                  placeholder="esquadria"
                />
              </label>
              {/* O back-end normaliza o critério (minúsculas, sem acento, sem espaço
                  nas pontas): o cliente vê exatamente o que vai ser gravado. */}
              {normalizedValue ? (
                <p className="card-label" data-testid="group-criterion-normalized">
                  {`Vai ser gravado como "${normalizedValue}".`}
                </p>
              ) : null}
            </>
          ) : null}

          <label className="ampli-group-editor__dynamic">
            <input
              type="checkbox"
              aria-label="Reavaliar a cada geração"
              checked={isExplicit ? false : dynamic}
              disabled={isReadOnly || isExplicit}
              onChange={(event) => { clearFeedback(); setDynamic(event.target.checked); }}
            />
            <span>Reavaliar a cada geração (pega insumo novo que casar com a descrição)</span>
          </label>

          <label>
            <span>Buscar insumo</span>
            <input
              type="search"
              aria-label="Buscar insumo"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Parte do nome do insumo"
            />
          </label>

          {isExplicit && explicitMembersMissing ? (
            <p className="alert alert-error" role="alert">
              Não foi possível carregar os insumos deste grupo. Recarregue a lista antes de
              editar.
            </p>
          ) : null}

          <fieldset className="ampli-group-editor__members">
            <legend>{membersLegend}</legend>
            {candidates.map((candidate) => (
              <CandidateRow
                key={candidate.key}
                candidate={candidate}
                selectable={isExplicit && !isReadOnly}
                selected={resourceIds.includes(resourceIdOf(candidate.key))}
                matches={
                  normalizedValue.length > 0
                  && normalizeText(candidate.label).includes(normalizedValue)
                }
                onToggle={toggleResource}
              />
            ))}
            {candidatesError ? (
              <p className="alert alert-error" role="alert">
                Não foi possível carregar os insumos da obra.
              </p>
            ) : null}
            {!candidatesError && candidates.length === 0 ? (
              <p className="empty-state">Nenhum insumo neste filtro.</p>
            ) : null}
          </fieldset>

          {overlap ? (
            <div className="alert alert-error" role="alert">
              <p>{`Alguns insumos já pertencem ao grupo "${overlap.name}".`}</p>
              {overlap.movable ? (
                <>
                  <p>Mover tira esses insumos do grupo atual e traz para este.</p>
                  {!isReadOnly ? (
                    <button
                      type="button"
                      className="button button-secondary small"
                      disabled={saving}
                      onClick={() => submit({ moveOnOverlap: true })}
                    >
                      Mover para este grupo
                    </button>
                  ) : null}
                </>
              ) : (
                <p>
                  Esse grupo é montado pela descrição, então mover não resolve: ajuste a
                  descrição ou os insumos deste grupo para não disputar os mesmos insumos.
                </p>
              )}
            </div>
          ) : null}

          {saveError ? <p className="alert alert-error" role="alert">{saveError}</p> : null}

          <div className="offcanvas-buttons">
            <button type="button" className="button button-secondary small" onClick={onClose}>
              {isReadOnly ? "Fechar" : "Cancelar"}
            </button>
            {!isReadOnly ? (
              <button
                type="button"
                className="button button-accent small"
                disabled={!canSave || saving}
                onClick={() => submit()}
              >
                {saving ? "Salvando…" : "Salvar grupo"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <div onClick={onClose} className="offcanvas-background" />
    </div>
  );
};

export default GroupEditorOffcanvas;
